import { useCallback, useRef } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { PLAN_VIEW_PLAN_TABLES_QUERY_KEY } from '@editor/pages/PlanView/hooks/usePlanViewPlanTables'
import {
  completePlanTableInput,
  planTableDtoToPlanTable,
  planTableToCreateDTO,
  planTableToModifyDTO,
} from '@editor/stores/planTableStore/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { clearPlanElementSelection, getSelectedPlanElement, usePlanTableStore } from '@editorStores'
import { getPlanTables } from '@queries'
import {
  createPlanTables,
  deletePlanTables,
  PlanTableCreateDTO,
  PlanTableDTO,
  updatePlanTable,
} from '@mutations'
import type { PlanTable, PlanTableInput, TableData } from 'src/domain/planTables/types'
import { isTemplatePlanTable } from 'src/domain/planTables/types'
import { usePersistentParams } from 'src/hooks'
import { needsPlanViewReresolve } from './utils'

const usePlanTableMutations = () => {
  const setTables = usePlanTableStore(state => state.setTables)
  const activeFilterViewId = usePlanTableStore(state => state.activeFilterViewId)
  const updateTableAttributes = usePlanTableStore(state => state.updateTableAttributes)
  const { projectId }: { projectId?: string } = usePersistentParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  const chainRef = useRef<Promise<unknown>>(Promise.resolve())

  const applyPlanTableResult = useCallback(
    (dtos: PlanTableDTO[]) => {
      const tables = dtos.map(planTableDtoToPlanTable)
      setTables(tables)
      // The response resolves template tables for fallback storeys; let the Planansicht
      // re-resolve them for the storeys it previews when their content may have changed.
      queryClient.invalidateQueries({
        queryKey: getPlanTables.getKey(projectId),
        predicate: query =>
          query.queryKey.includes(PLAN_VIEW_PLAN_TABLES_QUERY_KEY) &&
          needsPlanViewReresolve(query.state.data as PlanTableDTO[] | undefined, dtos),
      })

      const selectedPlanElement = getSelectedPlanElement()
      if (
        selectedPlanElement?.type === 'table' &&
        !tables.some(table => table.id === selectedPlanElement.id)
      ) {
        clearPlanElementSelection()
      }
    },
    [setTables, queryClient, projectId],
  )

  const createMutation = useMutation(
    (planTables: PlanTableCreateDTO[]) => createPlanTables.request(projectId, planTables),
    {
      mutationKey: ['createPlanTables', projectId],
      meta: {
        title: 'Tabelle erstellen',
        description: 'Erstellt Plan-Tabellen.',
      },
      onSuccess: planTables => applyPlanTableResult(planTables),
      onError: error =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Erstellen der Tabelle'),
          { variant: 'error' },
        ),
    },
  )

  const updateMutation = useMutation(
    (id: string) => {
      const planTable = usePlanTableStore.getState().tables.find(table => table.id === id)
      if (!planTable) return Promise.resolve(null)
      return updatePlanTable.request(projectId, id, planTableToModifyDTO(planTable))
    },
    {
      mutationKey: ['updatePlanTable', projectId],
      meta: {
        title: 'Tabelle aktualisieren',
        description: 'Speichert Änderungen an einer Plan-Tabelle.',
      },
      // Template tables are re-resolved on the server (e.g. after storeys change).
      // Apply that payload so the scene frame size matches the regenerated content.
      onSuccess: planTables => {
        if (planTables) applyPlanTableResult(planTables)
      },
      onError: async error => {
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Aktualisieren der Tabelle'),
          { variant: 'error' },
        )
        await queryClient.invalidateQueries({ queryKey: getPlanTables.getKey(projectId) })
      },
    },
  )

  const deleteMutation = useMutation((ids: string[]) => deletePlanTables.request(projectId, ids), {
    mutationKey: ['deletePlanTables', projectId],
    meta: {
      title: 'Tabelle löschen',
      description: 'Löscht Plan-Tabellen.',
    },
    onSuccess: planTables => applyPlanTableResult(planTables),
    onError: error =>
      enqueueSnackbar(buildErrorMessage(error as AxiosError, 'Fehler beim Löschen der Tabelle'), {
        variant: 'error',
      }),
  })

  const { mutateAsync: runCreate } = createMutation
  const { mutateAsync: runUpdate } = updateMutation
  const { mutate: runDelete } = deleteMutation

  const createPlanTableFromInput = useCallback(
    async (input: PlanTableInput) => {
      if (!activeFilterViewId) return null
      const previousIds = new Set(usePlanTableStore.getState().tables.map(table => table.id))
      const dto = planTableToCreateDTO(completePlanTableInput(input, activeFilterViewId))
      const result = await runCreate([dto])
      const createdDto = result?.find((table: PlanTableDTO) => !previousIds.has(table.id))
      return createdDto ? planTableDtoToPlanTable(createdDto) : null
    },
    [activeFilterViewId, runCreate],
  )

  const schedulePersist = useCallback(
    (id: string) => {
      const task = async () => {
        if (!usePlanTableStore.getState().tables.some(table => table.id === id)) return
        await runUpdate(id).catch(() => undefined)
      }

      chainRef.current = chainRef.current.catch(() => undefined).then(task)
    },
    [runUpdate],
  )

  const updatePlanTableFields = useCallback(
    (id: string, changes: Partial<PlanTable>) => {
      const planTable = usePlanTableStore.getState().tables.find(table => table.id === id)
      if (!planTable) return
      if (isTemplatePlanTable(planTable) && 'table' in changes) {
        const {
          table: _table,
          templateKey: _templateKey,
          tableType: _tableType,
          ...allowedChanges
        } = changes
        if (Object.keys(allowedChanges).length === 0) return
        updateTableAttributes(id, allowedChanges)
      } else {
        updateTableAttributes(id, changes)
      }
      schedulePersist(id)
    },
    [schedulePersist, updateTableAttributes],
  )

  const updatePlanTableData = useCallback(
    (id: string, updater: (table: TableData) => TableData) => {
      const planTable = usePlanTableStore.getState().tables.find(table => table.id === id)
      if (!planTable || isTemplatePlanTable(planTable)) return
      updatePlanTableFields(id, { table: updater(planTable.table) })
    },
    [updatePlanTableFields],
  )

  const deletePlanTableById = useCallback(
    (id: string) => {
      runDelete([id])
    },
    [runDelete],
  )

  const deletePlanTablesInActiveFilterView = useCallback(() => {
    if (!activeFilterViewId) return
    const ids = usePlanTableStore
      .getState()
      .tables.filter(table => table.filterViewId === activeFilterViewId)
      .map(table => table.id)
    if (ids.length === 0) return
    runDelete(ids)
  }, [activeFilterViewId, runDelete])

  return {
    createPlanTableFromInput,
    updatePlanTableFields,
    updatePlanTableData,
    deletePlanTable: deletePlanTableById,
    deletePlanTablesInActiveFilterView,
  }
}

export default usePlanTableMutations
